// characters: рантайм-состояние персонажей в матче — кто каким персонажем
// играет (Snapshot), макс. HP игроков, loadout локального игрока (Hello),
// параметры входа в комнату (?char=, ?lv=) и серебро гостя за сессию.

import { setLocalSpeedMultiplier } from "../engine/prediction";
import { characterById, isValidCharacterId } from "./registry";
import { creditGuestCoins, DEFAULT_RULES, guestLevelsParam, readLocalCharId } from "./progress";

const BASE_MAX_HP = 100;

/** id игрока → персонаж (из Snapshot). */
const playerCharIds = new Map<number, number>();
/** id игрока → макс. HP с апгрейдом (из Snapshot). */
const playerMaxHp = new Map<number, number>();

/** Loadout локального игрока из Hello (эффективные уровни). */
export interface LocalLoadout {
  charId: number;
  lvlHp: number;
  lvlSpeed: number;
  lvlArmor: number;
  speedMul: number;
}
let localLoadout: LocalLoadout | null = null;

/** Выбранный в меню персонаж (уходит в ?char=). `?char=N` в адресе — для
 *  отладки/ссылок: перекрывает сохранённый выбор на эту вкладку. */
let selectedCharId = ((): number => {
  const q = parseInt(new URLSearchParams(location.search).get("char") ?? "", 10);
  return isValidCharacterId(q) ? q : readLocalCharId();
})();

export function getSelectedCharId(): number {
  return selectedCharId;
}

export function setSelectedCharId(id: number): void {
  if (isValidCharacterId(id)) selectedCharId = id;
}

/** Персонаж игрока; до первого снапшота локальный — выбранный в меню. */
export function charIdOf(playerId: number, myId: number): number {
  const v = playerCharIds.get(playerId);
  if (v !== undefined) return v;
  if (playerId === myId) return localLoadout?.charId ?? selectedCharId;
  return 0;
}

export function maxHpOf(playerId: number): number {
  return playerMaxHp.get(playerId) ?? BASE_MAX_HP;
}

export function getLocalLoadout(): LocalLoadout | null {
  return localLoadout;
}

/** Параметры WS: персонаж всем, уровни — только гостю (аккаунту сервер берёт из БД). */
export function appendCharacterWsParams(params: URLSearchParams, guest: boolean): void {
  params.set("char", String(selectedCharId));
  if (guest) params.set("lv", guestLevelsParam());
}

/** Hello (characters-хвост): персонаж и множитель скорости для prediction. */
export function applyHelloCharacter(h: {
  charId?: number; lvlHp?: number; lvlSpeed?: number; lvlArmor?: number; speedMul?: number;
}): void {
  const speedMul = h.speedMul && h.speedMul > 0.5 && h.speedMul < 2 ? h.speedMul : 1;
  localLoadout = {
    charId: isValidCharacterId(h.charId ?? -1) ? h.charId! : selectedCharId,
    lvlHp: h.lvlHp ?? 0,
    lvlSpeed: h.lvlSpeed ?? 0,
    lvlArmor: h.lvlArmor ?? 0,
    speedMul,
  };
  setLocalSpeedMultiplier(speedMul);
}

// ─── Серебро гостя за SP-сессию ─────────────────────────────────────────
// Сервер начисляет только аккаунтам. Гостю считаем локально так же:
// бот = 2, не больше 40 за сессию (гость играет только SP против ботов).
let guestSessionKills = 0;
let guestSessionCredited = 0;
let lastMyScore = -1;

/** Новый вход в комнату: сессия заново, старый снапшот забыт. */
export function resetCharacterSession(): void {
  playerCharIds.clear();
  playerMaxHp.clear();
  localLoadout = null;
  guestSessionKills = 0;
  guestSessionCredited = 0;
  lastMyScore = -1;
  setLocalSpeedMultiplier(1);
}

/** Snapshot: персонажи и макс. HP; гостю — начисление серебра за фраги. */
export function noteSnapshotCharacters(
  players: readonly { id: number; score: number; charId?: number; maxHp?: number }[],
  myId: number,
  guest: boolean,
): void {
  for (const p of players) {
    if (p.charId !== undefined && isValidCharacterId(p.charId)) playerCharIds.set(p.id, p.charId);
    if (p.maxHp !== undefined && p.maxHp > 0) playerMaxHp.set(p.id, p.maxHp);
    if (guest && p.id === myId) {
      if (lastMyScore >= 0 && p.score > lastMyScore) {
        guestSessionKills += p.score - lastMyScore;
        const e = DEFAULT_RULES.earn;
        const due = Math.min(e.npcSessionCap, guestSessionKills * e.npcKill);
        if (due > guestSessionCredited) {
          creditGuestCoins(due - guestSessionCredited);
          guestSessionCredited = due;
        }
      }
      lastMyScore = p.score;
    }
  }
}

/** Подпись для HUD/лога: «Сыщица». */
export function characterNameOf(playerId: number, myId: number): string {
  return characterById(charIdOf(playerId, myId)).name;
}
